'use client';

import { motion, useInView } from 'framer-motion';
import { Check } from 'lucide-react';
import Image from 'next/image';
import React from 'react';

import { Separator } from '@/slw/shared/ui/seperator';

const benefits = [
  'Dedicated Dealer Account Specialists',
  'Proprietary Inventory Management',
  'Integrated Digital Marketing Support',
  'Full Vehicle History & Condition Reports',
  'Real-time Lead Management Analytics',
  'Seller Advertising & Marketing Tools',
];

export function DealerPartnerBenefitsSection() {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.section
      ref={ref}
      className='grid md:grid-cols-[1fr_1px_1fr] gap-12 items-center mb-14'
      initial='hidden'
      animate={inView ? 'visible' : 'hidden'}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
      }}
    >
      <motion.div
        variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.2 } } }}
        className='space-y-3'
      >
        <motion.h3
          className='text-xl md:text-2xl font-bold mb-12'
          initial={{ x: -40, opacity: 0 }}
          animate={inView ? { x: 0, opacity: 1 } : {}}
          transition={{ duration: 0.5 }}
        >
          Why the ClovisClassics.com Dealer Network?
        </motion.h3>
        <ul>
          {benefits.map((b, i) => (
            <motion.li
              key={b}
              className='flex items-center gap-3 text-sm font-medium mb-4'
              variants={{
                hidden: { opacity: 0, x: -20 },
                visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
              }}
            >
              <motion.span
                className='inline-flex items-center justify-center rounded-full bg-green-100 text-green-600 mr-2'
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ type: 'spring', stiffness: 400, damping: 20, delay: 0.2 + i * 0.1 }}
                aria-hidden
              >
                <Check className='w-5 h-5' />
              </motion.span>
              {b}
            </motion.li>
          ))}
        </ul>
      </motion.div>
      <div className='hidden md:block h-full'>
        <Separator orientation='vertical' className='w-[1px] h-full bg-gray-200' />
      </div>

      <motion.div
        className='flex rounded-sm justify-center overflow-hidden group'
        initial={{ x: 80, opacity: 0 }}
        animate={inView ? { x: 0, opacity: 1 } : {}}
        transition={{ type: 'spring', bounce: 0.3, duration: 0.8, delay: 0.3 }}
      >
        <Image
          className='object-cover object-center 
            transition-transform duration-300  group-hover:scale-110'
          src='/images/dealer-partner/dealer-partner-car.png'
          alt='Classic Car'
          width={632}
          height={298}
          priority
        />
      </motion.div>
    </motion.section>
  );
}
